/**
 * TransferPairInfo Component
 * Displays current transfer pair information and provides actions to manage it.
 * WP-TRANSFER-4: Reassign Transfer Pair
 *
 * Three visual states:
 * 1. Has transfer_pair_id - Fully paired transfer (green)
 * 2. Has target account but no pair - Target set but no matching transaction (yellow)
 * 3. No target account - Legacy transfer with no pairing info (gray)
 */
import { useMemo } from 'react';
import { Button } from '@/components/ui';
import { formatCurrency, formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';
import type { TransactionWithSplits } from '@/services/transactions';
import type { Account } from '@/types';

// ============================================================================
// Types
// ============================================================================

export interface TransferPairInfoProps {
  transaction: TransactionWithSplits;
  accounts: Account[];
  onReassign: () => void;
  onUnlink: () => void;
  onViewPairedTransaction?: (txnId: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export default function TransferPairInfo({
  transaction,
  accounts,
  onReassign,
  onUnlink,
  onViewPairedTransaction,
}: TransferPairInfoProps) {
  // Determine the visual state
  const hasPairedTransaction = !!transaction.transferPairId && !!transaction.transferPairTransaction;
  const hasTargetAccount = !!transaction.transferTargetAccountId;

  // Get the target account name from accounts list if not enriched
  const targetAccountName = useMemo(() => {
    if (transaction.transferTargetAccountName) {
      return transaction.transferTargetAccountName;
    }
    if (transaction.transferTargetAccountId) {
      const account = accounts.find((a) => a.id === transaction.transferTargetAccountId);
      return account?.name ?? 'Unknown Account';
    }
    return null;
  }, [transaction, accounts]);

  // State 1: Fully paired transfer
  if (hasPairedTransaction && transaction.transferPairTransaction) {
    const pairedTxn = transaction.transferPairTransaction;
    const isNegative = pairedTxn.amount < 0;

    return (
      <div className="mb-6 pt-4 border-t border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Transfer Pair
        </label>
        <div className="border border-green-200 bg-green-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-700">
                Paired with: {pairedTxn.accountName}
              </p>
              <div className="mt-1 flex items-center gap-2 text-sm text-green-600">
                <span
                  className={cn(
                    'font-medium',
                    isNegative ? 'text-gray-700' : 'text-green-700'
                  )}
                >
                  {isNegative ? '-' : '+'}
                  {formatCurrency(Math.abs(pairedTxn.amount), transaction.currency)}
                </span>
                <span className="text-green-500">on {formatDate(pairedTxn.date)}</span>
              </div>
              {onViewPairedTransaction && (
                <button
                  type="button"
                  onClick={() => onViewPairedTransaction(pairedTxn.id)}
                  className="mt-2 text-sm text-green-600 hover:text-green-700 underline flex items-center gap-1"
                >
                  <ExternalLinkIcon className="w-3.5 h-3.5" />
                  View Transaction
                </button>
              )}
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onReassign}
              className="flex-1"
              leftIcon={<RefreshIcon className="w-4 h-4" />}
            >
              Reassign Pair
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onUnlink}
              className="flex-1"
              leftIcon={<UnlinkIcon className="w-4 h-4" />}
            >
              Unlink Pair
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // State 2: Has target account but no paired transaction
  if (hasTargetAccount) {
    return (
      <div className="mb-6 pt-4 border-t border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Transfer Pair
        </label>
        <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <WarningIcon className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-yellow-700">
                Target Account: {targetAccountName}
              </p>
              <p className="mt-1 text-sm text-yellow-600">
                No matching transaction found
              </p>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onReassign}
              className="flex-1"
              leftIcon={<RefreshIcon className="w-4 h-4" />}
            >
              Reassign Pair
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onReassign}
              className="flex-1"
              leftIcon={<LinkIcon className="w-4 h-4" />}
            >
              Link Transaction
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // State 3: No target account set (legacy transfer)
  return (
    <div className="mb-6 pt-4 border-t border-gray-200">
      <label className="block text-sm font-medium text-gray-700 mb-3">
        Transfer Pair
      </label>
      <div className="border border-gray-200 bg-gray-50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <InfoIcon className="w-5 h-5 text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-500">
              No paired account set
            </p>
            <p className="mt-1 text-sm text-gray-400">
              Assign an account to track this transfer
            </p>
          </div>
        </div>
        <div className="mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onReassign}
            className="w-full"
            leftIcon={<PlusIcon className="w-4 h-4" />}
          >
            Assign Pair
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
      />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
      />
    </svg>
  );
}

function UnlinkIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.181 8.68l1.5-1.5m0 0l1.5-1.5m-1.5 1.5L13.18 5.18m1.5 1.5l1.5 1.5M5.82 15.68l-1.5 1.5m0 0l-1.5 1.5m1.5-1.5l1.5-1.5m-1.5 1.5l-1.5-1.5m8.485-8.485a4.5 4.5 0 016.364 6.364l-1.06 1.06-6.364-6.364 1.06-1.06z"
      />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
      />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
      />
    </svg>
  );
}
